import { db } from '../../../firebase';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  deleteField,
  deleteDoc,
  DocumentReference,
  DocumentData,
  collection,
} from 'firebase/firestore';
import { User } from 'firebase/auth';
import { HouseholdMember, Role } from '../../../../src/types';
import { Household } from '@/types';

/**
 * 🛰️ HYBRID SCOPING ARCHITECTURE
 * * Personal Scope: /users/{uid}/{collection}
 * Shared Scope:   /households/{hid}/{collection}
 * * This service manages the bridge between these partitions.
 */

/**
 * Initializes a new household and associates it with the current user.
 * Personal data remains in /users/{uid}, while this creates the shared hub.
 */
export async function initializeHousehold(
  user: User,
  householdName: string
): Promise<Household> {
  if (!user || !user.uid) {
    console.warn("initializeHousehold: User not authenticated."); // Added logging
    throw new Error('User not authenticated.');
  }

  const householdId = doc(collection(db, 'households')).id;

  const ownerMember: HouseholdMember = {
    id: user.uid,
    userId: user.uid,
    name: user.displayName || user.email || 'New Member',
    email: user.email || '',
    role: Role.OWNER,
    joinedAt: new Date().toISOString(),
  };

  const newHousehold: Household = {
    id: householdId,
    name: householdName,
    ownerId: user.uid,
    memberIds: [user.uid],
    memberLimit: 6,
    type: 'FAMILY',
    members: [ownerMember],
    permissions: {
      [user.uid]: {
        canManageTasks: true,
        canDeleteTasks: true,
        canManageEvents: true,
        canManageGroceries: true,
        canDeleteGroceries: true,
        canManageInventory: true,
        canManageContacts: true,
        canViewAuditLogs: true,
        role: Role.OWNER,
      },
    },
    address: null,
    activeInvite: null,
  };

  try {
    const householdRef = doc(db, 'households', householdId);
    await setDoc(householdRef, newHousehold);

    const userRef: DocumentReference<DocumentData> = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      await updateDoc(userRef, {
        memberOf: arrayUnion(householdId),
        currentHouseholdId: householdId,
        updatedAt: new Date().toISOString()
      });
    } else {
      await setDoc(userRef, {
        uid: user.uid,
        email: user.email,
        memberOf: [householdId],
        currentHouseholdId: householdId,
        createdAt: new Date().toISOString()
      });
    }

    return newHousehold;
  } catch (error: any) { // Explicitly type error as any for .code access if needed
    console.error('Error initializing household in service (re-throwing):', error.message, error.code, error); // More detailed error logging
      throw new Error(`Failed to initialize household: ${error.message || 'Unknown error'}`); // Re-throw with more detail
  }
}

/**
 * Leaves a household.
 * - Removes user from household's member list and permissions.
 * - Updates user's personal profile (memberOf, currentHouseholdId).
 */
export async function leaveHousehold(householdId: string, userId: string): Promise<void> {
  if (!householdId || !userId) {
    throw new Error('Household ID and User ID are required to leave a household.');
  }

  const householdRef = doc(db, 'households', householdId);
  const userRef = doc(db, 'users', userId);

  try {
    const householdDoc = await getDoc(householdRef);
    if (!householdDoc.exists()) {
      throw new Error('Household not found.');
    }
    const householdData = householdDoc.data() as Household;

    // Prevent owner from leaving via this function if other members exist
    if (householdData.ownerId === userId && householdData.memberIds.length > 1) {
      throw new Error('Owner must transfer ownership before leaving a non-empty household.');
    }

    // Update household document
    await updateDoc(householdRef, {
      memberIds: arrayRemove(userId),
      members: arrayRemove(householdData.members.find(m => m.userId === userId)),
      [`permissions.${userId}`]: deleteField(), // Remove user's permissions
    });

        // Update user document
    const userDocSnap = await getDoc(userRef);
    if (userDocSnap.exists()) {
      const userData = userDocSnap.data();
      await updateDoc(userRef, {
        memberOf: arrayRemove(householdId),
        // If leaving the current household, clear currentHouseholdId or set to null
        currentHouseholdId: (userData.currentHouseholdId === householdId) ? null : userData.currentHouseholdId,
      });
    }


  } catch (error: any) {
    console.error('Error leaving household:', error);
    throw new Error(`Failed to leave household: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Transfers ownership of a household to another member.
 * Only the current owner can initiate this.
 */
export async function transferHouseholdOwnership(
  householdId: string,
  currentOwnerId: string,
  newOwnerId: string
): Promise<void> {
  if (!householdId || !currentOwnerId || !newOwnerId) {
    throw new Error('All IDs are required for ownership transfer.');
  }
  if (currentOwnerId === newOwnerId) {
    throw new Error('New owner cannot be the same as the current owner.');
  }

  const householdRef = doc(db, 'households', householdId);

  try {
    const householdDoc = await getDoc(householdRef);
    if (!householdDoc.exists()) {
      throw new Error('Household not found.');
    }
    const householdData = householdDoc.data() as Household;

    if (householdData.ownerId !== currentOwnerId) {
      throw new Error('Unauthorized: Only the current owner can transfer ownership.');
    }
    if (!householdData.memberIds.includes(newOwnerId)) {
      throw new Error('New owner must be an existing member of the household.');
    }

    // Update household ownerId
    await updateDoc(householdRef, {
      ownerId: newOwnerId,
      // Update permissions: new owner gets OWNER role, old owner becomes MEMBER
      [`permissions.${newOwnerId}.role`]: Role.OWNER,
      [`permissions.${currentOwnerId}.role`]: Role.MEMBER,
      // Update roles in the 'members' array
      members: householdData.members.map(member => {
        if (member.userId === newOwnerId) {
          return { ...member, role: Role.OWNER };
        }
        if (member.userId === currentOwnerId) {
          return { ...member, role: Role.MEMBER };
        }
        return member;
      }),
    });
  } catch (error: any) {
    console.error('Error transferring household ownership:', error);
    throw new Error(`Failed to transfer ownership: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Deletes a household and cleans up associated user data.
 * Only callable by the owner if they are the only member.
 */
export async function deleteHousehold(householdId: string, ownerId: string): Promise<void> {
  if (!householdId || !ownerId) {
    throw new Error('Household ID and Owner ID are required to delete a household.');
  }

  const householdRef = doc(db, 'households', householdId);

  try {
    const householdDoc = await getDoc(householdRef);
    if (!householdDoc.exists()) {
      throw new Error('Household not found.');
    }
    const householdData = householdDoc.data() as Household;

    if (householdData.ownerId !== ownerId) {
      throw new Error('Unauthorized: Only the owner can delete the household.');
    }
    if (householdData.memberIds.length > 1) {
      throw new Error('Cannot delete a household with multiple members. Ownership must be transferred first or all other members must leave.');
    }

    // Delete the household document
    await deleteDoc(householdRef);

    // Remove householdId from the owner's memberOf array and clear currentHouseholdId
    const ownerRef = doc(db, 'users', ownerId);
    await updateDoc(ownerRef, {
      memberOf: arrayRemove(householdId),
      currentHouseholdId: null,
    });

    // Optionally: If there are other collections tied to this household (e.g., tasks, events)
    // you would need to implement a batch deletion here or rely on Firestore rules/Cloud Functions
    // to cascade deletes. For now, we only handle the household document itself.

  } catch (error: any) {
    console.error('Error deleting household:', error);
    throw new Error(`Failed to delete household: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Joins a household. Adheres to the household's permission matrix.
 */
export async function joinHouseholdWithCode(user: User, inviteCode: string): Promise<Household> {
  if (!user || !user.uid) {
    throw new Error('User not authenticated.');
  }
  if (!inviteCode || inviteCode.trim() === '') {
    throw new Error('Invite code cannot be empty.');
  }

  const cleanCode = inviteCode.toUpperCase().trim();
  const inviteRef = doc(db, 'invites', cleanCode);

  try {
    const inviteSnap = await getDoc(inviteRef);

    if (!inviteSnap.exists()) {
      throw new Error('Invalid or non-existent invite code.');
    }

    const inviteData = inviteSnap.data() as { householdId: string; expiresAt: string; };

    if (Date.now() > new Date(inviteData.expiresAt).getTime()) {
      throw new Error('This invite has expired.');
    }

    const householdRef = doc(db, 'households', inviteData.householdId);
    const householdSnap = await getDoc(householdRef);

    if (!householdSnap.exists()) {
      throw new Error('Target household does not exist.');
    }

    const household = { ...householdSnap.data(), id: householdSnap.id } as Household;

    if (household.memberIds.includes(user.uid)) {
      throw new Error('You are already a member of this household.');
    }

    if (household.memberIds.length >= (household.memberLimit || 6)) {
      throw new Error('This household has reached its member limit.');
    }

    const newMemberProfile: HouseholdMember = {
      id: user.uid,
      userId: user.uid,
      name: user.displayName || user.email || 'New Member',
      email: user.email || '',
      role: Role.GUEST,
      joinedAt: new Date().toISOString(),
    };

    // 🔒 Update Shared Scope
    // lastInviteCodeUsed travels with this write so firestore.rules' isValidSelfJoin() can
    // verify (via get()) that a real, unexpired invite for THIS household was actually used —
    // without it, the rule has no way to distinguish a legitimate join from someone who just
    // knows the household's Firestore doc id.
    await updateDoc(householdRef, {
      memberIds: arrayUnion(user.uid),
      members: arrayUnion(newMemberProfile),
      lastInviteCodeUsed: cleanCode,
    });

    // 🔒 Update Personal Scope Metadata
    const userRef: DocumentReference<DocumentData> = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      await updateDoc(userRef, {
        memberOf: arrayUnion(inviteData.householdId),
        currentHouseholdId: inviteData.householdId,
      });
    } else {
      await setDoc(userRef, {
        uid: user.uid,
        memberOf: [inviteData.householdId],
        currentHouseholdId: inviteData.householdId,
      });
    }

    const updatedHouseholdSnap = await getDoc(householdRef);
    return { ...updatedHouseholdSnap.data(), id: updatedHouseholdSnap.id } as Household;

  } catch (error) {
    console.error('Error joining household by code:', error);
    throw error;
  }
}

/**
 * ⚖️ SCOPE MANAGEMENT: updateMemberLimit
 * Restricted to Shared Scope owners.
 */
export async function updateMemberLimit(
  householdId: string,
  newLimit: number,
  currentUserId: string
): Promise<void> {
  if (newLimit <= 0) {
    throw new Error('Member limit must be a positive number.');
  }

  const householdRef = doc(db, 'households', householdId);

  try {
    const householdDoc = await getDoc(householdRef);

    if (!householdDoc.exists()) {
      throw new Error('Household not found.');
    }

    const householdData = householdDoc.data() as Household;

    // Authorization check against the household's permission matrix/owner status
    if (householdData.ownerId !== currentUserId) {
      throw new Error('Unauthorized: Only the household owner can change the member limit.');
    }

    await updateDoc(householdRef, {
      memberLimit: newLimit,
    });
  } catch (error) {
    console.error('Error updating member limit:', error);
    throw new Error('Failed to update member limit.');
  }
}